(function (global) {
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const TOGGLE_SCALE = 2.5;
  const DOUBLE_TAP_MS = 280;
  const DOUBLE_TAP_DIST = 36;
  const DRAG_THRESH = 8;
  const SWIPE_THRESH = 50;

  class AtlasImageZoom {
    constructor(stage, image, options = {}) {
      this.stage = stage;
      this.image = image;
      this.ignoreSelector = options.ignoreSelector || 'button, a';
      this.onSwipe = typeof options.onSwipe === 'function' ? options.onSwipe : null;
      this.onBackdropTap = typeof options.onBackdropTap === 'function' ? options.onBackdropTap : null;
      this.ignoreBackdropUntil = 0;
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pointers = new Map();
      this.pinch = null;
      this.drag = null;
      this.moved = false;
      this.didPinch = false;
      this.lastTap = null;
      this.bound = false;
      this._pd = this.#onPointerDown.bind(this);
      this._pm = this.#onPointerMove.bind(this);
      this._pu = this.#onPointerUp.bind(this);
      this._wh = this.#onWheel.bind(this);
    }

    get zoomed() {
      return this.scale > 1.02;
    }

    attach() {
      if (this.bound) return;
      this.bound = true;
      this.stage.addEventListener('pointerdown', this._pd);
      this.stage.addEventListener('pointermove', this._pm);
      this.stage.addEventListener('pointerup', this._pu);
      this.stage.addEventListener('pointercancel', this._pu);
      this.stage.addEventListener('wheel', this._wh, { passive: false });
      this.apply(false);
    }

    detach() {
      if (!this.bound) return;
      this.bound = false;
      this.stage.removeEventListener('pointerdown', this._pd);
      this.stage.removeEventListener('pointermove', this._pm);
      this.stage.removeEventListener('pointerup', this._pu);
      this.stage.removeEventListener('pointercancel', this._pu);
      this.stage.removeEventListener('wheel', this._wh);
      this.reset();
    }

    reset() {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pointers.clear();
      this.pinch = null;
      this.drag = null;
      this.moved = false;
      this.didPinch = false;
      this.apply(false);
    }

    apply(animate) {
      this.#constrain();
      this.image.style.transition = animate ? 'transform 0.22s ease' : 'none';
      this.image.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
      this.stage.classList.toggle('is-zoomed', this.zoomed);
      this.stage.classList.toggle('is-dragging', Boolean(this.drag && this.moved));
    }

    #center() {
      const rect = this.stage.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
        w: rect.width,
        h: rect.height,
      };
    }

    #zoomTo(nextScale, clientX, clientY, animate) {
      const old = this.scale || MIN_SCALE;
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
      const center = this.#center();
      const px = clientX - center.x;
      const py = clientY - center.y;
      const ratio = next / old;
      this.tx += px * (1 - ratio);
      this.ty += py * (1 - ratio);
      this.scale = next;
      if (this.scale <= MIN_SCALE) {
        this.scale = MIN_SCALE;
        this.tx = 0;
        this.ty = 0;
      }
      this.apply(animate);
    }

    #toggle(clientX, clientY) {
      if (this.zoomed) this.#zoomTo(MIN_SCALE, clientX, clientY, true);
      else this.#zoomTo(TOGGLE_SCALE, clientX, clientY, true);
    }

    #constrain() {
      const center = this.#center();
      const width = (this.image.offsetWidth || 0) * this.scale;
      const height = (this.image.offsetHeight || 0) * this.scale;
      const maxX = Math.max(0, (width - center.w) / 2);
      const maxY = Math.max(0, (height - center.h) / 2);
      this.tx = Math.min(maxX, Math.max(-maxX, this.tx));
      this.ty = Math.min(maxY, Math.max(-maxY, this.ty));
    }

    #onWheel(event) {
      if (event.target instanceof Element && event.target.closest(this.ignoreSelector)) return;
      event.preventDefault();
      const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
      this.#zoomTo(this.scale * factor, event.clientX, event.clientY, false);
    }

    #onPointerDown(event) {
      if (event.target instanceof Element && event.target.closest(this.ignoreSelector)) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.moved = false;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      try {
        this.stage.setPointerCapture(event.pointerId);
      } catch (error) {}

      if (this.pointers.size === 2) {
        const points = [...this.pointers.values()];
        this.pinch = {
          dist: Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) || 1,
          scale: this.scale,
        };
        this.didPinch = true;
        this.drag = null;
        return;
      }

      this.drag = {
        x: event.clientX,
        y: event.clientY,
        tx: this.tx,
        ty: this.ty,
        onImage: Boolean(event.target.closest('img') || event.target === this.image),
      };
    }

    #onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2 && this.pinch) {
        const points = [...this.pointers.values()];
        const dist = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        const midX = (points[0].x + points[1].x) / 2;
        const midY = (points[0].y + points[1].y) / 2;
        this.#zoomTo(this.pinch.scale * (dist / this.pinch.dist), midX, midY, false);
        this.moved = true;
        return;
      }

      if (!this.drag) return;

      const dx = event.clientX - this.drag.x;
      const dy = event.clientY - this.drag.y;
      if (Math.hypot(dx, dy) > DRAG_THRESH) this.moved = true;

      if (this.zoomed) {
        this.tx = this.drag.tx + dx;
        this.ty = this.drag.ty + dy;
        this.apply(false);
      }
    }

    #onPointerUp(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinch = null;

      if (this.pointers.size > 0) return;

      const drag = this.drag;
      this.drag = null;
      this.stage.classList.remove('is-dragging');

      if (!drag) return;

      if (this.didPinch) {
        this.didPinch = false;
        return;
      }

      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      const dist = Math.hypot(dx, dy);

      if (!this.zoomed && dist >= SWIPE_THRESH && Math.abs(dx) > Math.abs(dy)) {
        if (this.onSwipe) this.onSwipe(dx < 0 ? 1 : -1);
        this.lastTap = null;
        return;
      }

      if (dist > DRAG_THRESH || this.moved) return;

      if (drag.onImage) {
        const now = Date.now();
        const last = this.lastTap;
        const isMouse = event.pointerType === 'mouse';
        const isDouble =
          last &&
          now - last.time < DOUBLE_TAP_MS &&
          Math.hypot(event.clientX - last.x, event.clientY - last.y) < DOUBLE_TAP_DIST;

        if (isMouse || isDouble) {
          this.#toggle(event.clientX, event.clientY);
          this.lastTap = null;
          return;
        }

        this.lastTap = { time: now, x: event.clientX, y: event.clientY };
        return;
      }

      if (Date.now() < this.ignoreBackdropUntil) return;
      if (this.onBackdropTap) this.onBackdropTap();
    }
  }

  function lockPage() {
    document.documentElement.classList.add('atlas-lightbox-open');
  }

  function unlockPage() {
    if (!document.querySelector('dialog.atlas-lightbox[open]')) {
      document.documentElement.classList.remove('atlas-lightbox-open');
    }
  }

  function getGalleryItems(gallery) {
    return Array.from(gallery.querySelectorAll('[data-atlas-zoom-item]')).map((item) => ({
      button: item,
      full_url: item.getAttribute('data-full-url') || '',
      full_srcset: item.getAttribute('data-full-srcset') || '',
      alt: item.getAttribute('data-alt') || item.querySelector('img')?.alt || '',
    })).filter((item) => item.full_url);
  }

  function setupGalleryLightbox(gallery, dialog) {
    const stage = dialog.querySelector('[data-atlas-lightbox-stage]');
    const imageEl = dialog.querySelector('[data-atlas-lightbox-image]');
    const closeBtn = dialog.querySelector('[data-atlas-lightbox-close]');
    const prevBtn = dialog.querySelector('[data-atlas-lightbox-prev]');
    const nextBtn = dialog.querySelector('[data-atlas-lightbox-next]');
    if (!(stage instanceof HTMLElement) || !(imageEl instanceof HTMLImageElement)) return;

    const zoom = new AtlasImageZoom(stage, imageEl, {
      onSwipe: (delta) => step(delta),
      onBackdropTap: () => closeLightbox(),
    });
    zoom.attach();

    let index = 0;
    let requestedSrc = '';
    let trackPointer = null;

    function items() {
      return getGalleryItems(gallery);
    }

    function isOpen() {
      return dialog.open || dialog.hasAttribute('open');
    }

    function syncArrows() {
      const list = items();
      const hasMany = list.length > 1;
      if (prevBtn instanceof HTMLElement) prevBtn.hidden = !hasMany || index <= 0;
      if (nextBtn instanceof HTMLElement) nextBtn.hidden = !hasMany || index >= list.length - 1;
    }

    function render(nextIndex) {
      const list = items();
      const item = list[nextIndex];
      if (!item) return;
      index = nextIndex;
      zoom.reset();
      syncArrows();
      imageEl.alt = item.alt || '';
      imageEl.sizes = '100vw';

      const preview = item.button.querySelector('img');
      if (preview instanceof HTMLImageElement && preview.currentSrc && imageEl.src !== item.full_url) {
        imageEl.removeAttribute('srcset');
        imageEl.src = preview.currentSrc;
      }

      requestedSrc = item.full_url;
      const upgrade = new Image();
      upgrade.decoding = 'async';
      if (item.full_srcset) upgrade.srcset = item.full_srcset;
      upgrade.sizes = '100vw';
      upgrade.onload = () => {
        if (requestedSrc !== item.full_url) return;
        if (item.full_srcset) imageEl.srcset = item.full_srcset;
        else imageEl.removeAttribute('srcset');
        imageEl.src = item.full_url;
      };
      upgrade.src = item.full_url;
    }

    function closeLightbox() {
      zoom.reset();
      if (isOpen() && typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      unlockPage();
    }

    function openLightbox(nextIndex) {
      const list = items();
      if (!list.length) return;
      render(Math.max(0, Math.min(list.length - 1, nextIndex)));
      lockPage();
      zoom.ignoreBackdropUntil = Date.now() + 350;
      try {
        dialog.showModal();
      } catch (error) {
        dialog.setAttribute('open', '');
      }
      if (closeBtn instanceof HTMLElement) closeBtn.focus({ preventScroll: true });
    }

    function step(delta) {
      const list = items();
      const nextIndex = index + delta;
      if (nextIndex < 0 || nextIndex >= list.length) return;
      render(nextIndex);
    }

    function onClick(event) {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-atlas-lightbox-close]')) {
        event.preventDefault();
        closeLightbox();
        return;
      }
      if (event.target.closest('[data-atlas-lightbox-prev]')) {
        event.preventDefault();
        step(-1);
        return;
      }
      if (event.target.closest('[data-atlas-lightbox-next]')) {
        event.preventDefault();
        step(1);
      }
    }

    function onKeyDown(event) {
      if (!isOpen()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeLightbox();
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        step(-1);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        step(1);
      }
    }

    gallery.addEventListener('pointerdown', (event) => {
      const item = event.target instanceof Element ? event.target.closest('[data-atlas-zoom-item]') : null;
      if (!item || !gallery.contains(item)) {
        trackPointer = null;
        return;
      }
      const track = gallery.querySelector('[data-pfg-track]');
      trackPointer = {
        x: event.clientX,
        scroll: track instanceof HTMLElement ? track.scrollLeft : 0,
        item,
      };
    });

    gallery.addEventListener('click', (event) => {
      const item = event.target instanceof Element ? event.target.closest('[data-atlas-zoom-item]') : null;
      if (!item || !gallery.contains(item)) return;
      const start = trackPointer;
      trackPointer = null;
      const track = gallery.querySelector('[data-pfg-track]');
      if (start && Math.abs(event.clientX - start.x) > DRAG_THRESH) return;
      if (start && track instanceof HTMLElement && Math.abs(track.scrollLeft - start.scroll) > DRAG_THRESH) return;
      event.preventDefault();
      const list = items();
      const nextIndex = list.findIndex((entry) => entry.button === item);
      openLightbox(nextIndex === -1 ? 0 : nextIndex);
    });

    dialog.addEventListener('click', onClick);
    dialog.addEventListener('keydown', onKeyDown);
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeLightbox();
    });
    dialog.addEventListener('close', unlockPage);
  }

  function initZoomGalleries() {
    document.querySelectorAll('[data-atlas-zoom-gallery]').forEach((gallery) => {
      if (!(gallery instanceof HTMLElement) || gallery.dataset.zoomReady === 'true') return;
      const target = gallery.getAttribute('data-lightbox-target');
      const dialog = target
        ? document.querySelector(target)
        : gallery.querySelector('dialog.atlas-lightbox');
      if (!(dialog instanceof HTMLDialogElement)) return;
      gallery.dataset.zoomReady = 'true';
      setupGalleryLightbox(gallery, dialog);
    });
  }

  global.AtlasImageZoom = AtlasImageZoom;
  global.initAtlasZoomGalleries = initZoomGalleries;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initZoomGalleries);
  } else {
    initZoomGalleries();
  }
  document.addEventListener('shopify:section:load', initZoomGalleries);
})(window);
